import { Group, Paper, Tabs, Text, Title } from "@mantine/core";
import { IconCheck, IconQuestionMark, IconX } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import {
  useMatch,
  useNavigate,
  useResolvedPath,
  useSearchParams
} from "react-router";
import { useSiteOverview } from "../../hooks/useSiteOverview";
import { useSiteStatusOverview } from "../../hooks/useSiteStatusOverview";
import urls from "../../urls";
import { TodoStatus } from "../../utils/backend-types";
import { THEME_VARS } from "../../utils/constants";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { useShouldShowStaffView } from "../StaffViewToggle/useShouldShowStaffView";
import type {
  MeteringConceptInfo,
  OnboardingTodo,
  SiteOverviewSite,
  SiteStatusLogs,
  SiteStatusSite
} from "./site-status-types";
import { OnboardingTodosGroup } from "./site-status-types";
import "./SiteStatus.scss";
import { SiteStatusControls } from "./SiteStatusControls";
import { SiteStatusModal } from "./SiteStatusModal";
import { SiteStatusTable } from "./SiteStatusTable/SiteStatusTable";

enum SiteStatusTabs {
  Active = "aktiv",
  Inactive = "inaktiv"
}

interface SiteStatusProps {
  sites?: Array<SiteStatusSite> | Array<SiteOverviewSite>;
  loading?: boolean;
  title?: string;
  simple?: boolean;
  showProjectLinks?: boolean;
}

function SiteStatus({
  sites,
  loading,
  title,
  simple,
  showProjectLinks
}: SiteStatusProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { shouldShowStaffView, isLoading: isStaffViewLoading } =
    useShouldShowStaffView();
  const [showDemoProjects, setShowDemoProjects] = useState(false);
  const [onboardingTodosModalTodos, setOnboardingTodosModalTodos] =
    useState<Array<OnboardingTodo> | null>(null);
  const [logsModalLogs, setLogsModalLogs] = useState<SiteStatusLogs | null>(
    null
  );
  const isOnboardingTodosModalOpen =
    !!onboardingTodosModalTodos && onboardingTodosModalTodos.length > 0;
  const [meteringConceptInfo, setMeteringConceptInfo] =
    useState<MeteringConceptInfo | null>(null);
  const isLogsModalOpen = !!logsModalLogs;
  const isMeteringConceptInfoModalOpen = !!meteringConceptInfo;

  const isLoading = loading || isStaffViewLoading;

  const filteredSites = useMemo(
    () =>
      (sites || []).filter(
        (site) =>
          site.projectIsDemo === showDemoProjects ||
          (!showDemoProjects && !site.projectIsDemo)
      ),
    [sites, showDemoProjects]
  );
  const activeSites = useMemo(
    () => filteredSites.filter((site) => site.projectIsActive),
    [filteredSites]
  );
  const inactiveSites = useMemo(
    () => filteredSites.filter((site) => !site.projectIsActive),
    [filteredSites]
  );

  const handleOpenOnboardingTodosModal = useCallback(
    (todos: Array<OnboardingTodo>) => {
      setOnboardingTodosModalTodos(todos);
    },
    []
  );

  function handleCloseOnboardingTodosModal() {
    setOnboardingTodosModalTodos(null);
  }

  const handleOpenLogsModal = useCallback((logs: SiteStatusLogs) => {
    setLogsModalLogs(logs);
  }, []);

  function handleCloseLogsModal() {
    setLogsModalLogs(null);
  }

  const handleOpenMeteringConceptInfoCellModal = useCallback(
    (mbkInfo: MeteringConceptInfo) => {
      setMeteringConceptInfo(mbkInfo);
    },
    []
  );

  function handleCloseMeteringConceptInfoModal() {
    setMeteringConceptInfo(null);
  }

  function handleProjectCreated(projectId: string) {
    navigate(urls.managerProjectView(projectId));
  }

  const storageKey = simple ? "SiteOverviewTable" : "SiteStatusOverviewTable";

  const showTabs = inactiveSites.length > 0;
  const activeTab =
    showTabs && searchParams.get(SiteStatusTabs.Inactive) !== null
      ? SiteStatusTabs.Inactive
      : SiteStatusTabs.Active;

  return (
    <div className="SiteStatus">
      <Paper>
        <Group justify="space-between" variant="paper-header">
          <Title variant="paper-header-font">{title}</Title>
          <SiteStatusControls
            showDemoProjects={showDemoProjects}
            showDemoProjectsToggle={shouldShowStaffView}
            onCreateProject={handleProjectCreated}
            onToggleDemoProjects={setShowDemoProjects}
          />
        </Group>
        <Tabs value={activeTab} onChange={(newTab) => navigate(`.?${newTab}`)}>
          {showTabs && (
            <Tabs.List mb={0}>
              <Tabs.Tab component="a" value={SiteStatusTabs.Active}>
                Aktive Projekte
              </Tabs.Tab>
              <Tabs.Tab component="a" value={SiteStatusTabs.Inactive}>
                Inaktive Projekte
              </Tabs.Tab>
            </Tabs.List>
          )}
          <LoadOrError loading={isLoading}>
            <Tabs.Panel value={SiteStatusTabs.Active}>
              {activeTab === SiteStatusTabs.Active && (
                <SiteStatusTable
                  showCategoriesColumn={shouldShowStaffView}
                  showDemoProjects={showDemoProjects}
                  showProjectLinks={showProjectLinks}
                  simple={simple}
                  sites={activeSites}
                  storageKey={storageKey}
                  tab={SiteStatusTabs.Active}
                  onClickLogsCell={handleOpenLogsModal}
                  onClickMeteringConceptInfoCell={
                    handleOpenMeteringConceptInfoCellModal
                  }
                  onClickOnboardingTodosCell={handleOpenOnboardingTodosModal}
                />
              )}
            </Tabs.Panel>
            <Tabs.Panel value={SiteStatusTabs.Inactive}>
              {activeTab === SiteStatusTabs.Inactive && (
                <SiteStatusTable
                  showCategoriesColumn={shouldShowStaffView}
                  showDemoProjects={showDemoProjects}
                  showProjectLinks={showProjectLinks}
                  simple={simple}
                  sites={inactiveSites}
                  storageKey={storageKey}
                  tab={SiteStatusTabs.Inactive}
                  onClickLogsCell={handleOpenLogsModal}
                  onClickMeteringConceptInfoCell={
                    handleOpenMeteringConceptInfoCellModal
                  }
                  onClickOnboardingTodosCell={handleOpenOnboardingTodosModal}
                />
              )}
            </Tabs.Panel>
          </LoadOrError>
        </Tabs>
      </Paper>
      <OnboardingTodosModal
        isOpen={isOnboardingTodosModalOpen}
        todos={onboardingTodosModalTodos}
        onClose={handleCloseOnboardingTodosModal}
      />
      <MeteringConceptInfoModal
        isOpen={isMeteringConceptInfoModalOpen}
        mbkInfo={meteringConceptInfo}
        onClose={handleCloseMeteringConceptInfoModal}
      />
      <LogsModal
        isOpen={isLogsModalOpen}
        logs={logsModalLogs}
        onClose={handleCloseLogsModal}
      />
    </div>
  );
}

interface OnboardingTodoModalProps {
  todos: Array<OnboardingTodo> | null;
  isOpen: boolean;
  onClose: () => void;
}

function OnboardingTodosModal({
  todos,
  isOpen,
  onClose
}: OnboardingTodoModalProps) {
  const safeTodos = todos || [];
  const noGroupTodos = safeTodos.filter((todo) => !todo.group);
  const meteringConceptCreationRequirementTodos = safeTodos.filter(
    (todo) =>
      todo.group === OnboardingTodosGroup.MeteringConceptCreationRequirement
  );
  const meteringConceptCreationStatusTodos = safeTodos.filter(
    (todo) => todo.group === OnboardingTodosGroup.MeteringConceptCreationStatus
  );

  return (
    <SiteStatusModal
      className="onboarding-todos-modal"
      header="Status Onboarding-Aufgaben"
      isOpen={isOpen}
      onClose={onClose}
    >
      <TodosGroup
        title="Voraussetzung für Messkonzepterstellung"
        todos={meteringConceptCreationRequirementTodos}
      />
      <TodosGroup
        title="Status der Messkonzepterstellung"
        todos={meteringConceptCreationStatusTodos}
      />
      <TodosGroup title="Weitere Aufgaben" todos={noGroupTodos} />
    </SiteStatusModal>
  );
}

interface MeteringConceptInfoModalProps {
  mbkInfo: MeteringConceptInfo | null;
  isOpen: boolean;
  onClose: () => void;
}

function MeteringConceptInfoModal({
  mbkInfo,
  isOpen,
  onClose
}: MeteringConceptInfoModalProps) {
  return (
    <SiteStatusModal
      header="Letzte MBK Fehlermeldung"
      isOpen={isOpen}
      onClose={onClose}
    >
      <p style={{ whiteSpace: "pre-wrap" }}>
        {mbkInfo?.errorMessages.join("\n")}
      </p>
    </SiteStatusModal>
  );
}

interface LogsModalProps {
  logs: SiteStatusLogs | null;
  isOpen: boolean;
  onClose: () => void;
}

function LogsModal({ logs, isOpen, onClose }: LogsModalProps) {
  return (
    <SiteStatusModal
      header="Hinweise aus der Verbrauchsanalyse"
      isOpen={isOpen}
      onClose={onClose}
    >
      <Title order={6}>Infos</Title>
      <ul>
        {logs && logs.infos && logs.infos.length > 0 ? (
          logs.infos.map((info, index) => <li key={index}>{info}</li>)
        ) : (
          <li>Keine Infos</li>
        )}
      </ul>
      <Title order={6}>Warnungen</Title>
      <ul>
        {logs && logs.warnings && logs.warnings.length > 0 ? (
          logs.warnings.map((warning, index) => <li key={index}>{warning}</li>)
        ) : (
          <li>Keine Warnungen</li>
        )}
      </ul>
      <Title order={6}>Blocker</Title>
      <ul>
        {logs && logs.errors && logs.errors.length > 0 ? (
          logs.errors.map((error, index) => <li key={index}>{error}</li>)
        ) : (
          <li>Keine Blocker</li>
        )}
      </ul>
    </SiteStatusModal>
  );
}

interface TodosGroupProps {
  todos: Array<OnboardingTodo>;
  title: string;
}

function TodosGroup({ todos, title }: TodosGroupProps) {
  if (!todos || todos.length === 0) {
    return (
      <TodosList title={title}>
        <li className="onboarding-todo-list-item">
          <IconQuestionMark size="1.3em" /> <Text ml="xs">Keine Aufgaben</Text>
        </li>
      </TodosList>
    );
  }

  return <TodosList title={title}>{todos.map(TodoListItem)}</TodosList>;
}

interface TodoListProps {
  title: string;
  children: React.ReactNode;
}

function TodosList({ title, children }: TodoListProps) {
  return (
    <>
      {title && <Title order={6}>{title}</Title>}
      <ul className="onboarding-todo-list">{children}</ul>
    </>
  );
}

function TodoListItem(todo: OnboardingTodo, index: number) {
  return (
    <li className="onboarding-todo-list-item" key={index}>
      {todo.status === TodoStatus.Done ? (
        <IconCheck color={THEME_VARS.success[6]} size="1.3em" />
      ) : (
        <IconX color={THEME_VARS.error[6]} size="1.3rem" />
      )}{" "}
      <Text ml="xs">{todo.label}</Text>
    </li>
  );
}

function SiteStatusOverview({ title = "Ihre Projekte" }) {
  const resolved = useResolvedPath(":year");
  const match = useMatch({ path: resolved.pathname, end: true });
  const year = match && match.params.year ? match.params.year : undefined;

  const {
    data: sites,
    isLoading,
    error
  } = useSiteStatusOverview(year ? Number(year) : undefined);

  return (
    <LoadOrError error={error}>
      <SiteStatus loading={isLoading} sites={sites} title={title} />
    </LoadOrError>
  );
}

function SiteOverview({ title = "Ihre Projekte" }) {
  const { data: sites, isLoading, error } = useSiteOverview();

  return (
    <LoadOrError error={error}>
      <SiteStatus
        loading={isLoading}
        showProjectLinks
        simple
        sites={sites}
        title={title}
      />
    </LoadOrError>
  );
}

export { SiteOverview, SiteStatus, SiteStatusOverview, SiteStatusTabs };
